import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import ModalFactura, { estadoFactura } from './ModalFactura';
import ModalNotaVenta from './ModalNotaVenta';
import ModalConfiguracionRecibo from './ModalConfiguracionRecibo';
import ModalReciboVenta, { obtenerReciboVenta } from './ModalReciboVenta';

const BASE_FACTURAS = '/api/facturacion/facturas';
const BASE_NOTAS = '/api/facturacion/notas-venta';
const dinero = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const TODAS = '__todas__';

// Helper para comparar sucursales de manera flexible (ej. "Tulcán Sur" vs "Sur")
const coincideSucursal = (itemSucursal, tab) => {
    if (tab === TODAS) return true;
    const s = (itemSucursal || '').toLowerCase();
    const t = (tab || '').toLowerCase();
    if (t.includes('sur')) return s.includes('sur');
    if (t.includes('nor')) return s.includes('nor') || s.includes('matriz');
    return s === t;
};

// Modal de selección de paciente (para Factura o Nota de Venta)
const ElegirPaciente = ({ onElegir, onSinPaciente, onClose, titulo = 'Nueva factura: elija el paciente' }) => {
    const [pacientes, setPacientes] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        api.get('/api/patients/', { params: { limit: 2000 } })
            .then(r => setPacientes(r.data.results || r.data || []))
            .catch(() => setPacientes([]))
            .finally(() => setCargando(false));
    }, []);

    const q = busqueda.trim().toLowerCase();
    const visibles = pacientes.filter(p => !q || [p.first_name, p.last_name, p.cedula, p.phone]
        .some(v => (v || '').toLowerCase().includes(q))).slice(0, 100);

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.7)', zIndex: 10040, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={{ background: '#fff', borderRadius: 16, width: 720, maxWidth: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ background: '#111827', color: '#fff', padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: '1.05rem' }}><i className="bi bi-person-check me-2"></i>{titulo}</strong>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                </div>
                <div style={{ padding: '14px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input autoFocus value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre, cédula o teléfono..."
                        style={{ flex: 1, minWidth: 220, padding: '9px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: '0.95rem' }} />
                    <button onClick={onSinPaciente} style={{ padding: '9px 14px', borderRadius: 8, border: '1.5px solid #111827', background: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                        Sin paciente / consumidor final
                    </button>
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                    {cargando ? <div style={{ padding: 20, color: '#6b7280' }}>Cargando pacientes...</div> : visibles.length === 0 ? (
                        <div style={{ padding: 20, color: '#6b7280' }}>No se encontraron pacientes.</div>
                    ) : visibles.map(p => (
                        <div key={p.id} onClick={() => onElegir(p)}
                            style={{ padding: '11px 22px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 10 }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }} onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                            <span><strong>{p.first_name} {p.last_name}</strong><span style={{ color: '#6b7280', fontSize: '0.85rem' }}> · C.I. {p.cedula || 'S/C'}</span></span>
                            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>{p.email || p.phone || ''}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Pantalla principal: Facturación Electrónica (SRI) y Notas de Venta (Interna)
const Facturacion = () => {
    const [tipoDoc, setTipoDoc] = useState('facturas'); // 'facturas' | 'notas'
    const [facturas, setFacturas] = useState([]);
    const [notasVenta, setNotasVenta] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [tabFactura, setTabFactura] = useState(TODAS);
    const [tabNota, setTabNota] = useState(TODAS);
    const [cargando, setCargando] = useState(true);
    const [filtro, setFiltro] = useState('');
    const [estado, setEstado] = useState('');

    // Flujo Nueva Factura
    const [pasoFactura, setPasoFactura] = useState(null); // null | 'paciente' | 'factura'
    const [pacienteFactura, setPacienteFactura] = useState(null);

    // Flujo Nueva Nota de Venta
    const [pasoNota, setPasoNota] = useState(null); // null | 'paciente' | 'nota'
    const [pacienteNota, setPacienteNota] = useState(null);

    // Modal para ver/reimprimir nota de venta existente
    const [notaSeleccionadaVer, setNotaSeleccionadaVer] = useState(null);

    const [actualizando, setActualizando] = useState(null);
    const [miSucursal, setMiSucursal] = useState(null);

    // Talonario / Recibo de Venta Oficial
    const [mostrarConfigRecibo, setMostrarConfigRecibo] = useState(false);
    const [reciboParaVer, setReciboParaVer] = useState(null);
    const [cargandoReciboId, setCargandoReciboId] = useState(null);

    const verRecibo = async (base, id) => {
        setCargandoReciboId(id);
        try {
            const recibo = await obtenerReciboVenta(base, id);
            if (recibo) setReciboParaVer(recibo);
        } catch (e) {
            alert(e.response?.data?.detail || 'No se pudo cargar el recibo.');
        } finally {
            setCargandoReciboId(null);
        }
    };

    // Eliminación
    const [facturaParaEliminar, setFacturaParaEliminar] = useState(null);
    const [notaParaEliminar, setNotaParaEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);

    const ejecutarEliminarFactura = async () => {
        if (!facturaParaEliminar) return;
        setEliminando(true);
        try {
            const { data } = await api.delete(`${BASE_FACTURAS}/${facturaParaEliminar.id}/`);
            setFacturas(prev => prev.filter(x => x.id !== facturaParaEliminar.id));
            alert(data?.detail || 'Factura eliminada de forma interna. El stock ha sido restablecido.');
            setFacturaParaEliminar(null);
        } catch (e) {
            alert(e.response?.data?.detail || 'No se pudo eliminar la factura.');
        }
        setEliminando(false);
    };

    const ejecutarEliminarNota = async () => {
        if (!notaParaEliminar) return;
        setEliminando(true);
        try {
            const { data } = await api.delete(`${BASE_NOTAS}/${notaParaEliminar.id}/`);
            setNotasVenta(prev => prev.filter(x => x.id !== notaParaEliminar.id));
            alert(data?.detail || 'Nota de venta eliminada permanentemente. El stock ha sido restablecido.');
            setNotaParaEliminar(null);
        } catch (e) {
            alert(e.response?.data?.detail || 'No se pudo eliminar la nota de venta.');
        }
        setEliminando(false);
    };

    const cargar = async () => {
        setCargando(true);
        try {
            const [f, s, nv] = await Promise.all([
                api.get(`${BASE_FACTURAS}/`),
                api.get(`${BASE_FACTURAS}/sucursales/`),
                api.get(`${BASE_NOTAS}/`).catch(() => ({ data: [] }))
            ]);
            setFacturas(f.data.results || f.data || []);
            setSucursales(s.data || []);
            setNotasVenta(nv.data.results || nv.data || []);
        } catch (e) {
            console.error('Error cargando comprobantes:', e);
        }
        setCargando(false);
    };

    useEffect(() => {
        cargar();
        api.get(`${BASE_FACTURAS}/opciones/`).then(r => setMiSucursal(r.data)).catch(() => {});
    }, []);

    const actualizarFactura = async (f) => {
        setActualizando(f.id);
        try {
            const { data } = await api.post(`${BASE_FACTURAS}/${f.id}/actualizar/`);
            setFacturas(prev => prev.map(x => (x.id === f.id ? data.factura : x)));
        } catch (e) {
            alert(e.response?.data?.detail || 'No se pudo consultar el estado.');
        }
        setActualizando(null);
    };

    const anularNota = async (nv) => {
        if (!window.confirm(`¿Está seguro de anular la nota de venta ${nv.numero}?\n\nSi contenía productos del inventario, el stock será devuelto automáticamente.`)) {
            return;
        }
        try {
            const { data } = await api.post(`${BASE_NOTAS}/${nv.id}/anular/`);
            setNotasVenta(prev => prev.map(x => (x.id === nv.id ? (data.nota || { ...x, estado: 'ANULADA' }) : x)));
            alert(data.detail || 'Nota de venta anulada.');
        } catch (e) {
            alert(e.response?.data?.detail || 'No se pudo anular la nota de venta.');
        }
    };

    // Datos y filtros según la pestaña activa
    const texto = filtro.trim().toLowerCase();

    // 1. Facturas
    const deLaPestanaFacturas = facturas.filter(f => coincideSucursal(f.sucursal, tabFactura));
    const visiblesFacturas = deLaPestanaFacturas.filter(f =>
        (!estado || (estado === 'pendiente' ? !['AUTHORIZED', 'ERROR', 'REJECTED'].includes(f.estado) : f.estado === estado)) &&
        (!texto || [f.numero, f.cliente_nombre, f.cliente_identificacion, f.clave_acceso, f.emitida_por].some(v => (v || '').toLowerCase().includes(texto)))
    );
    const autorizadas = deLaPestanaFacturas.filter(f => f.estado === 'AUTHORIZED');
    const totalAutorizado = autorizadas.reduce((a, f) => a + Number(f.total || 0), 0);
    const infoTabFactura = sucursales.find(s => coincideSucursal(s.sucursal, tabFactura));

    // 2. Notas de Venta
    const deLaPestanaNotas = notasVenta.filter(n => coincideSucursal(n.sucursal, tabNota));
    const visiblesNotas = deLaPestanaNotas.filter(n =>
        (!estado || n.estado === estado) &&
        (!texto || [n.numero, n.cliente_nombre, n.cliente_identificacion, n.emitida_por, n.observaciones].some(v => (v || '').toLowerCase().includes(texto)))
    );
    const notasVigentes = deLaPestanaNotas.filter(n => n.estado !== 'ANULADA');
    const totalNotasVigentes = notasVigentes.reduce((a, n) => a + Number(n.total || 0), 0);
    const notasAnuladas = deLaPestanaNotas.filter(n => n.estado === 'ANULADA');

    return (
        <div style={{ padding: 24, maxWidth: 1300, margin: '0 auto' }}>
            <style>{`
                .fe-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 16px 18px; }
                .fe-kpis { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
                .fe-kpi-v { font-size: 1.5rem; font-weight: 800; color: #111827; }
                .fe-kpi-l { font-size: 0.78rem; color: #6b7280; font-weight: 600; text-transform: uppercase; }
                .fe-tabs { display: flex; gap: 4px; border-bottom: 2px solid #e5e7eb; margin-bottom: 16px; overflow-x: auto; }
                .fe-tab { border: none; background: transparent; padding: 10px 16px; font-weight: 700; font-size: 0.92rem; color: #6b7280; cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px; white-space: nowrap; transition: all 0.15s ease; }
                .fe-tab.on { color: #111827; border-bottom-color: #b59a4a; }
                .fe-tab.on-nv { color: #065f46; border-bottom-color: #059669; }
                .fe-tab .n { display: inline-block; margin-left: 6px; background: #f3f4f6; color: #374151; border-radius: 999px; padding: 0 8px; font-size: 0.75rem; }
                .fe-tabla { width: 100%; border-collapse: collapse; font-size: 0.87rem; }
                .fe-tabla th { text-align: left; color: #6b7280; font-size: 0.74rem; font-weight: 700; padding: 8px; border-bottom: 1px solid #e5e7eb; }
                .fe-tabla td { padding: 9px 8px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
                .fe-pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 0.76rem; font-weight: 700; white-space: nowrap; }
                .fe-btn { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 7px; font-size: 0.78rem; font-weight: 600; border: 1px solid #d1d5db; background: #fff; color: #111827; cursor: pointer; text-decoration: none; }
                .fe-in { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.9rem; }
                @media (max-width: 760px) { .fe-kpis { grid-template-columns: 1fr; } }
            `}</style>

            {/* Encabezado Superior con Acciones Principales */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
                <div>
                    <h2 style={{ fontWeight: 800, margin: 0, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className={tipoDoc === 'facturas' ? 'bi bi-receipt-cutoff' : 'bi bi-file-earmark-text'}></i>
                        {tipoDoc === 'facturas' ? 'Facturación Electrónica' : 'Notas de Venta'}
                    </h2>
                    <div style={{ color: '#6b7280', marginTop: 2 }}>
                        {tipoDoc === 'facturas' ? (
                            <>
                                Facturación electrónica SRI (KoaDoc).
                                {miSucursal && (miSucursal.configurado
                                    ? <> Usted factura desde <strong>{miSucursal.sucursal || 'su sucursal'}</strong>.</>
                                    : <span style={{ color: '#b45309', fontWeight: 600 }}> Su usuario aún no tiene punto de emisión SRI.</span>)}
                            </>
                        ) : (
                            <>
                                Notas de venta internas para venta directa y control de inventario (no se envían al SRI).
                            </>
                        )}
                    </div>
                </div>

                {/* Botones de Acción: Recibo Oficial, Nueva Nota de Venta y Nueva Factura */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                        onClick={() => setMostrarConfigRecibo(true)}
                        style={{
                            padding: '10px 14px', borderRadius: 8, border: '1.5px solid #cbd5e1',
                            background: '#fff', color: '#1e293b', fontWeight: 700,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                        }}
                        title="Configurar numeración de talonario / recibo físico"
                    >
                        <i className="bi bi-gear-fill" style={{ color: '#64748b' }}></i>
                        <span>N° Recibo</span>
                    </button>

                    <button
                        onClick={() => setPasoNota('paciente')}
                        style={{
                            padding: '10px 18px', borderRadius: 8, border: 'none',
                            background: '#059669', color: '#fff', fontWeight: 700,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)'
                        }}
                    >
                        <i className="bi bi-plus-lg"></i>
                        <span>Nueva nota de venta</span>
                    </button>

                    <button
                        onClick={() => setPasoFactura('paciente')}
                        style={{
                            padding: '10px 18px', borderRadius: 8, border: 'none',
                            background: '#111827', color: '#fff', fontWeight: 700,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            boxShadow: '0 2px 4px rgba(17, 24, 39, 0.2)'
                        }}
                    >
                        <i className="bi bi-plus-lg"></i>
                        <span>Nueva factura</span>
                    </button>
                </div>
            </div>

            {/* Pestañas Principales (Nivel 1): Facturas SRI vs Notas de Venta */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '2px solid #e5e7eb' }}>
                <button
                    type="button"
                    onClick={() => { setTipoDoc('facturas'); setFiltro(''); setEstado(''); }}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '12px 20px', borderRadius: '10px 10px 0 0',
                        border: 'none',
                        borderBottom: tipoDoc === 'facturas' ? '3px solid #111827' : '3px solid transparent',
                        background: tipoDoc === 'facturas' ? '#f3f4f6' : 'transparent',
                        color: tipoDoc === 'facturas' ? '#111827' : '#6b7280',
                        fontWeight: 800, fontSize: '0.98rem', cursor: 'pointer',
                        marginBottom: -2, transition: 'all 0.15s ease'
                    }}
                >
                    <i className="bi bi-receipt-cutoff" style={{ fontSize: '1.1rem' }}></i>
                    Facturas (SRI)
                    <span style={{
                        background: tipoDoc === 'facturas' ? '#111827' : '#e5e7eb',
                        color: tipoDoc === 'facturas' ? '#fff' : '#4b5563',
                        borderRadius: '999px', padding: '1px 8px', fontSize: '0.75rem', fontWeight: 700
                    }}>
                        {facturas.length}
                    </span>
                </button>

                <button
                    type="button"
                    onClick={() => { setTipoDoc('notas'); setFiltro(''); setEstado(''); }}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '12px 20px', borderRadius: '10px 10px 0 0',
                        border: 'none',
                        borderBottom: tipoDoc === 'notas' ? '3px solid #059669' : '3px solid transparent',
                        background: tipoDoc === 'notas' ? '#ecfdf5' : 'transparent',
                        color: tipoDoc === 'notas' ? '#065f46' : '#6b7280',
                        fontWeight: 800, fontSize: '0.98rem', cursor: 'pointer',
                        marginBottom: -2, transition: 'all 0.15s ease'
                    }}
                >
                    <i className="bi bi-file-earmark-text" style={{ fontSize: '1.1rem' }}></i>
                    Notas de Venta (Interna)
                    <span style={{
                        background: tipoDoc === 'notas' ? '#059669' : '#e5e7eb',
                        color: tipoDoc === 'notas' ? '#fff' : '#4b5563',
                        borderRadius: '999px', padding: '1px 8px', fontSize: '0.75rem', fontWeight: 700
                    }}>
                        {notasVenta.length}
                    </span>
                </button>
            </div>

            {/* Sub-pestañas por Sucursal (Nivel 2) */}
            {tipoDoc === 'facturas' ? (
                <div>
                    <div className="fe-tabs">
                        <button className={`fe-tab ${tabFactura === TODAS ? 'on' : ''}`} onClick={() => setTabFactura(TODAS)}>
                            Todas<span className="n">{facturas.length}</span>
                        </button>
                        <button className={`fe-tab ${tabFactura === 'Tulcán Sur' ? 'on' : ''}`} onClick={() => setTabFactura('Tulcán Sur')}>
                            <i className="bi bi-shop me-1"></i>Tulcán Sur
                            <span className="n">{facturas.filter(f => coincideSucursal(f.sucursal, 'Tulcán Sur')).length}</span>
                        </button>
                        <button className={`fe-tab ${tabFactura === 'Tulcán Norte' ? 'on' : ''}`} onClick={() => setTabFactura('Tulcán Norte')}>
                            <i className="bi bi-shop me-1"></i>Tulcán Norte
                            <span className="n">{facturas.filter(f => coincideSucursal(f.sucursal, 'Tulcán Norte')).length}</span>
                        </button>
                    </div>
                    {infoTabFactura && (
                        <div style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: -6, marginBottom: 12 }}>
                            Usuarios de esta sucursal: <strong>{infoTabFactura.usuarios?.join(', ') || '—'}</strong>
                            {!infoTabFactura.con_token && infoTabFactura.usuarios?.length > 0 && <span style={{ color: '#b45309' }}> · ninguno tiene token asignado</span>}
                        </div>
                    )}
                </div>
            ) : (
                <div>
                    <div className="fe-tabs">
                        <button className={`fe-tab ${tabNota === TODAS ? 'on-nv' : ''}`} onClick={() => setTabNota(TODAS)}>
                            Todas<span className="n">{notasVenta.length}</span>
                        </button>
                        <button className={`fe-tab ${tabNota === 'Tulcán Sur' ? 'on-nv' : ''}`} onClick={() => setTabNota('Tulcán Sur')}>
                            <i className="bi bi-shop me-1"></i>Tulcán Sur
                            <span className="n">{notasVenta.filter(n => coincideSucursal(n.sucursal, 'Tulcán Sur')).length}</span>
                        </button>
                        <button className={`fe-tab ${tabNota === 'Tulcán Norte' ? 'on-nv' : ''}`} onClick={() => setTabNota('Tulcán Norte')}>
                            <i className="bi bi-shop me-1"></i>Tulcán Norte
                            <span className="n">{notasVenta.filter(n => coincideSucursal(n.sucursal, 'Tulcán Norte')).length}</span>
                        </button>
                    </div>
                    {tabNota !== TODAS && (
                        <div style={{ color: '#065f46', fontSize: '0.85rem', marginTop: -6, marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
                            <span>
                                <i className="bi bi-geo-alt me-1"></i>
                                {tabNota.toLowerCase().includes('sur')
                                    ? 'AV. VEINTIMILLA Y CAMILO PONCE · Tel: 0993387008'
                                    : 'Entre Arellano y Ayacucho · Tel: 0962602999'}
                            </span>
                            <span style={{ color: '#6b7280' }}>· Descuento automático de stock sin SRI</span>
                        </div>
                    )}
                </div>
            )}

            {/* Tarjetas KPI */}
            {tipoDoc === 'facturas' ? (
                <div className="fe-kpis">
                    <div className="fe-card">
                        <div className="fe-kpi-l">Facturas autorizadas (SRI)</div>
                        <div className="fe-kpi-v">{autorizadas.length}</div>
                    </div>
                    <div className="fe-card">
                        <div className="fe-kpi-l">Total facturado autorizado</div>
                        <div className="fe-kpi-v">{dinero(totalAutorizado)}</div>
                    </div>
                    <div className="fe-card">
                        <div className="fe-kpi-l">En proceso o con error</div>
                        <div className="fe-kpi-v">{deLaPestanaFacturas.length - autorizadas.length}</div>
                    </div>
                </div>
            ) : (
                <div className="fe-kpis">
                    <div className="fe-card">
                        <div className="fe-kpi-l">Notas de venta emitidas</div>
                        <div className="fe-kpi-v" style={{ color: '#065f46' }}>{notasVigentes.length}</div>
                    </div>
                    <div className="fe-card">
                        <div className="fe-kpi-l">Total vendido (vigente)</div>
                        <div className="fe-kpi-v" style={{ color: '#065f46' }}>{dinero(totalNotasVigentes)}</div>
                    </div>
                    <div className="fe-card">
                        <div className="fe-kpi-l">Notas anuladas</div>
                        <div className="fe-kpi-v" style={{ color: '#991b1b' }}>{notasAnuladas.length}</div>
                    </div>
                </div>
            )}

            {/* Tabla y Filtros */}
            <div className="fe-card">
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                    <input
                        className="fe-in"
                        style={{ flex: 1, minWidth: 220 }}
                        placeholder={tipoDoc === 'facturas'
                            ? "Buscar por número, cliente, cédula, usuario o clave de acceso..."
                            : "Buscar por número de nota, cliente, cédula, usuario o notas..."}
                        value={filtro}
                        onChange={e => setFiltro(e.target.value)}
                    />
                    {tipoDoc === 'facturas' ? (
                        <select className="fe-in" value={estado} onChange={e => setEstado(e.target.value)}>
                            <option value="">Todos los estados</option>
                            <option value="AUTHORIZED">Autorizadas</option>
                            <option value="pendiente">En proceso</option>
                            <option value="ERROR">Con error</option>
                            <option value="REJECTED">Rechazadas</option>
                        </select>
                    ) : (
                        <select className="fe-in" value={estado} onChange={e => setEstado(e.target.value)}>
                            <option value="">Todos los estados</option>
                            <option value="EMITIDA">Emitidas (Vigentes)</option>
                            <option value="ANULADA">Anuladas</option>
                        </select>
                    )}
                    <button className="fe-btn" onClick={cargar}>
                        <i className="bi bi-arrow-clockwise"></i>Recargar
                    </button>
                </div>

                {cargando ? (
                    <div style={{ color: '#6b7280', padding: '20px 0' }}>Cargando comprobantes...</div>
                ) : tipoDoc === 'facturas' ? (
                    /* TABLA DE FACTURAS ELECTRÓNICAS */
                    visiblesFacturas.length === 0 ? (
                        <div style={{ color: '#6b7280', padding: '20px 0' }}>
                            No hay facturas{texto || estado ? ' con ese filtro' : ' en esta pestaña'}.
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="fe-tabla">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Nº factura</th>
                                        <th>Cliente</th>
                                        {tabFactura === TODAS && <th>Sucursal</th>}
                                        <th>Emitida por</th>
                                        <th style={{ textAlign: 'right' }}>Total</th>
                                        <th>Estado SRI</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visiblesFacturas.map(f => {
                                        const est = estadoFactura(f);
                                        return (
                                            <tr key={f.id}>
                                                <td style={{ whiteSpace: 'nowrap' }}>{new Date(f.created_at).toLocaleDateString()}</td>
                                                <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{f.numero || '—'}</td>
                                                <td>{f.cliente_nombre}<div style={{ color: '#6b7280', fontSize: '0.78rem' }}>{f.cliente_identificacion}</div></td>
                                                {tabFactura === TODAS && <td>{f.sucursal || 'Sin sucursal'}</td>}
                                                <td style={{ color: '#374151' }}>{f.emitida_por || '—'}<div style={{ color: '#6b7280', fontSize: '0.75rem' }}>{f.origen_display}</div></td>
                                                <td style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(f.total)}</td>
                                                <td>
                                                    <span className="fe-pill" style={{ background: est.fondo, color: est.color }}>{est.texto}</span>
                                                    {f.error_mensaje && <div style={{ color: '#991b1b', fontSize: '0.75rem', marginTop: 3, maxWidth: 280 }}>{f.error_codigo ? `[${f.error_codigo}] ` : ''}{f.error_mensaje}</div>}
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                        <button
                                                            className="fe-btn"
                                                            style={{ color: '#164e9e', borderColor: '#bfdbfe', background: '#eff6ff', fontWeight: 700 }}
                                                            onClick={() => verRecibo(BASE_FACTURAS, f.id)}
                                                            disabled={cargandoReciboId === f.id}
                                                            title="Ver / Imprimir el Recibo Oficial (Talonario) de esta factura"
                                                        >
                                                            <i className={cargandoReciboId === f.id ? "bi bi-arrow-clockwise" : "bi bi-file-earmark-check"}></i>
                                                            {cargandoReciboId === f.id ? 'Cargando...' : 'Recibo'}
                                                        </button>
                                                        {f.pdf_url && <a className="fe-btn" href={f.pdf_url} target="_blank" rel="noopener noreferrer"><i className="bi bi-file-earmark-pdf"></i>PDF</a>}
                                                        {f.xml_url && <a className="fe-btn" href={f.xml_url} target="_blank" rel="noopener noreferrer"><i className="bi bi-filetype-xml"></i>XML</a>}
                                                        {f.koadoc_id && f.estado !== 'AUTHORIZED' && (
                                                            <button className="fe-btn" onClick={() => actualizarFactura(f)} disabled={actualizando === f.id}>
                                                                 <i className="bi bi-arrow-clockwise"></i>{actualizando === f.id ? '...' : 'Estado'}
                                                            </button>
                                                        )}
                                                        <button
                                                            className="fe-btn"
                                                            style={{ color: '#b91c1c', borderColor: '#fca5a5' }}
                                                            onClick={() => setFacturaParaEliminar(f)}
                                                            title="Eliminar factura de manera interna (restablece stock)"
                                                        >
                                                            <i className="bi bi-trash"></i>Eliminar
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : (
                    /* TABLA DE NOTAS DE VENTA */
                    visiblesNotas.length === 0 ? (
                        <div style={{ color: '#6b7280', padding: '20px 0' }}>
                            No hay notas de venta{texto || estado ? ' con ese filtro' : ' en esta pestaña'}.
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className="fe-tabla">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Nº Nota de Venta</th>
                                        <th>Cliente</th>
                                        {tabNota === TODAS && <th>Sucursal</th>}
                                        <th>Emitida por</th>
                                        <th style={{ textAlign: 'right' }}>Total</th>
                                        <th>Estado</th>
                                        <th style={{ textAlign: 'right' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visiblesNotas.map(nv => {
                                        const esAnulada = nv.estado === 'ANULADA';
                                        return (
                                            <tr key={nv.id} style={{ opacity: esAnulada ? 0.65 : 1 }}>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {new Date(nv.created_at || nv.fecha_emision).toLocaleDateString()}
                                                </td>
                                                <td style={{ fontWeight: 800, whiteSpace: 'nowrap', color: '#065f46' }}>
                                                    {nv.numero || '—'}
                                                </td>
                                                <td>
                                                    <strong>{nv.cliente_nombre}</strong>
                                                    <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>C.I. {nv.cliente_identificacion}</div>
                                                </td>
                                                {tabNota === TODAS && <td>{nv.sucursal || 'Sin sucursal'}</td>}
                                                <td style={{ color: '#374151' }}>
                                                    {nv.emitida_por || '—'}
                                                    {nv.observaciones && <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>{nv.observaciones}</div>}
                                                </td>
                                                <td style={{ textAlign: 'right', fontWeight: 800, color: esAnulada ? '#991b1b' : '#065f46' }}>
                                                    {dinero(nv.total)}
                                                </td>
                                                <td>
                                                    {esAnulada ? (
                                                        <span className="fe-pill" style={{ background: '#fef2f2', color: '#991b1b' }}>ANULADA</span>
                                                    ) : (
                                                        <span className="fe-pill" style={{ background: '#ecfdf5', color: '#065f46' }}>EMITIDA</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                        <button
                                                            className="fe-btn"
                                                            style={{ color: '#164e9e', borderColor: '#bfdbfe', background: '#eff6ff', fontWeight: 700 }}
                                                            onClick={() => verRecibo(BASE_NOTAS, nv.id)}
                                                            disabled={cargandoReciboId === nv.id}
                                                            title="Ver / Imprimir el Recibo Oficial (Talonario) de esta nota de venta"
                                                        >
                                                            <i className={cargandoReciboId === nv.id ? "bi bi-arrow-clockwise" : "bi bi-file-earmark-check"}></i>
                                                            {cargandoReciboId === nv.id ? 'Cargando...' : 'Recibo'}
                                                        </button>
                                                        <button
                                                            className="fe-btn"
                                                            onClick={() => setNotaSeleccionadaVer(nv)}
                                                            title="Ver e imprimir comprobante térmico / recibo"
                                                        >
                                                            <i className="bi bi-printer"></i>Ver
                                                        </button>
                                                        {!esAnulada && (
                                                            <button
                                                                className="fe-btn"
                                                                style={{ color: '#d97706', borderColor: '#fde68a' }}
                                                                onClick={() => anularNota(nv)}
                                                                title="Anular nota de venta y devolver productos al inventario"
                                                            >
                                                                <i className="bi bi-x-circle"></i>Anular
                                                            </button>
                                                        )}
                                                        <button
                                                            className="fe-btn"
                                                            style={{ color: '#b91c1c', borderColor: '#fca5a5' }}
                                                            onClick={() => setNotaParaEliminar(nv)}
                                                            title="Eliminar nota de venta permanentemente (restablece stock)"
                                                        >
                                                            <i className="bi bi-trash"></i>Eliminar
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                )}
            </div>

            {/* Modal: Elegir Paciente para Factura */}
            {pasoFactura === 'paciente' && (
                <ElegirPaciente
                    titulo="Nueva factura: elija el paciente"
                    onElegir={(p) => { setPacienteFactura(p); setPasoFactura('factura'); }}
                    onSinPaciente={() => { setPacienteFactura(null); setPasoFactura('factura'); }}
                    onClose={() => setPasoFactura(null)}
                />
            )}

            {/* Modal: Emitir Factura Electrónica SRI */}
            {pasoFactura === 'factura' && (
                <ModalFactura
                    paciente={pacienteFactura}
                    origen={pacienteFactura ? 'paciente' : 'manual'}
                    onEmitida={() => cargar()}
                    onClose={() => { setPasoFactura(null); setPacienteFactura(null); cargar(); }}
                />
            )}

            {/* Modal: Elegir Paciente para Nota de Venta */}
            {pasoNota === 'paciente' && (
                <ElegirPaciente
                    titulo="Nueva nota de venta: elija el paciente"
                    onElegir={(p) => { setPacienteNota(p); setPasoNota('nota'); }}
                    onSinPaciente={() => { setPacienteNota(null); setPasoNota('nota'); }}
                    onClose={() => setPasoNota(null)}
                />
            )}

            {/* Modal: Emitir Nota de Venta (Interna) */}
            {pasoNota === 'nota' && (
                <ModalNotaVenta
                    paciente={pacienteNota}
                    sucursalUsuario={miSucursal?.sucursal}
                    origen={pacienteNota ? 'paciente' : 'manual'}
                    onEmitida={() => cargar()}
                    onClose={() => { setPasoNota(null); setPacienteNota(null); cargar(); }}
                />
            )}

            {/* Modal: Ver / Imprimir Nota de Venta Existente */}
            {notaSeleccionadaVer && (
                <ModalNotaVenta
                    notaExistente={notaSeleccionadaVer}
                    onClose={() => setNotaSeleccionadaVer(null)}
                />
            )}

            {/* Modal de Advertencia: Eliminar Factura (Interna) */}
            {facturaParaEliminar && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.7)', zIndex: 10050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 16, width: 540, maxWidth: '100%', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)' }}>
                        <div style={{ background: '#991b1b', color: '#fff', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <strong style={{ fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="bi bi-exclamation-triangle-fill"></i>
                                Eliminar Factura de forma Interna
                            </strong>
                            <button onClick={() => !eliminando && setFacturaParaEliminar(null)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                        </div>
                        <div style={{ padding: 22 }}>
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                                <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 6, fontSize: '0.95rem' }}>
                                    ⚠️ AVISO IMPORTANTE SOBRE EL SRI
                                </div>
                                <div style={{ fontSize: '0.88rem', color: '#7f1d1d', lineHeight: 1.45 }}>
                                    La factura se va a <strong>eliminar de manera interna</strong> en este sistema, <strong>más NO se va a anular en el SRI</strong>.
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#991b1b', marginTop: 8 }}>
                                    👉 Si desea anularla legalmente ante el SRI, debe ingresar a <a href="https://srienlinea.sri.gob.ec" target="_blank" rel="noopener noreferrer" style={{ color: '#1d4ed8', fontWeight: 700, textDecoration: 'underline' }}>SRI en Línea</a>.
                                </div>
                            </div>

                            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: '0.88rem', border: '1px solid #e2e8f0' }}>
                                <div><strong>Nº Factura:</strong> {facturaParaEliminar.numero || 'Sin número'}</div>
                                <div><strong>Cliente:</strong> {facturaParaEliminar.cliente_nombre} ({facturaParaEliminar.cliente_identificacion})</div>
                                <div><strong>Total:</strong> {dinero(facturaParaEliminar.total)}</div>
                                <div><strong>Sucursal:</strong> {facturaParaEliminar.sucursal || 'Sin sucursal'}</div>
                            </div>

                            <div style={{ fontSize: '0.86rem', color: '#065f46', background: '#ecfdf5', padding: '8px 12px', borderRadius: 6, border: '1px solid #a7f3d0', marginBottom: 18 }}>
                                <i className="bi bi-box-seam me-1"></i>
                                <strong>Restablecimiento de stock:</strong> El stock de todos los productos incluidos en esta factura será restablecido automáticamente en el inventario.
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button
                                    type="button"
                                    onClick={() => setFacturaParaEliminar(null)}
                                    disabled={eliminando}
                                    style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={ejecutarEliminarFactura}
                                    disabled={eliminando}
                                    style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                                >
                                    <i className="bi bi-trash"></i>
                                    {eliminando ? 'Eliminando...' : 'Sí, eliminar internamente'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Advertencia: Eliminar Nota de Venta */}
            {notaParaEliminar && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.7)', zIndex: 10050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 16, width: 500, maxWidth: '100%', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)' }}>
                        <div style={{ background: '#111827', color: '#fff', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <strong style={{ fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <i className="bi bi-exclamation-triangle-fill" style={{ color: '#f59e0b' }}></i>
                                Eliminar Nota de Venta
                            </strong>
                            <button onClick={() => !eliminando && setNotaParaEliminar(null)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                        </div>
                        <div style={{ padding: 22 }}>
                            <p style={{ fontSize: '0.98rem', fontWeight: 600, color: '#1f2937', margin: '0 0 12px' }}>
                                ¿Estás seguro de eliminar esta nota de venta?
                            </p>
                            <div style={{ fontSize: '0.88rem', color: '#4b5563', marginBottom: 16 }}>
                                Se eliminará permanentemente de los registros del sistema.
                            </div>

                            <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: '0.88rem', border: '1px solid #e2e8f0' }}>
                                <div><strong>Nº Nota:</strong> <span style={{ color: '#065f46', fontWeight: 700 }}>{notaParaEliminar.numero || 'Sin número'}</span></div>
                                <div><strong>Cliente:</strong> {notaParaEliminar.cliente_nombre} ({notaParaEliminar.cliente_identificacion})</div>
                                <div><strong>Total:</strong> {dinero(notaParaEliminar.total)}</div>
                                <div><strong>Sucursal:</strong> {notaParaEliminar.sucursal || 'Sin sucursal'}</div>
                            </div>

                            <div style={{ fontSize: '0.86rem', color: '#065f46', background: '#ecfdf5', padding: '8px 12px', borderRadius: 6, border: '1px solid #a7f3d0', marginBottom: 18 }}>
                                <i className="bi bi-box-seam me-1"></i>
                                <strong>Restablecimiento de stock:</strong> El stock de todos los productos incluidos en esta nota de venta será restablecido automáticamente en el inventario.
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <button
                                    type="button"
                                    onClick={() => setNotaParaEliminar(null)}
                                    disabled={eliminando}
                                    style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontWeight: 600, cursor: 'pointer' }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={ejecutarEliminarNota}
                                    disabled={eliminando}
                                    style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                                >
                                    <i className="bi bi-trash"></i>
                                    {eliminando ? 'Eliminando...' : 'Sí, eliminar permanentemente'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Configuración de numeración de recibo físico */}
            {mostrarConfigRecibo && (
                <ModalConfiguracionRecibo onClose={() => setMostrarConfigRecibo(false)} />
            )}


            {/* Modal: Ver Recibo de Compra existente */}
            {reciboParaVer && (
                <ModalReciboVenta
                    reciboExistente={reciboParaVer}
                    onClose={() => setReciboParaVer(null)}
                />
            )}
        </div>
    );
};

export default Facturacion;
